import * as React from 'react';

import { FlexLayout } from '../FlexLayout';
import { GridLayoutHeader } from '../../../constants';
import style from './GridLayout.module.scss';
import { JoinClassName } from '../../../utils/string';
import { tintedCellStyle } from '../../../utils/layout';

/**
 * 셀 안쪽 여백을 정하는 클래스.
 *
 * 예전에는 `AutoPadding` 이 **페이지가 만든 element 의 props 를 직접 고쳐**
 * (`p.style.paddingLeft = 12`) 여백을 넣거나, 없으면 div 를 하나 더 씌웠다.
 * props 를 고치는 것은 React 가 금지하는 일이고, 씌운 div 는 행마다 열 수만큼
 * 늘어나 구형 PC 에서 그대로 비용이 됐다. 같은 판단을 그대로 두되 **셀 자신의
 * 클래스**로 옮긴다 — 노드도 안 늘고 남의 element 도 안 건드린다.
 */
export function paddingClassOf(item: any): string | null {
  if (item?.type?.DOM_NAME !== 'FlexLayout') return null;
  const p = item.props;
  if (p.isVertical || p.align?.toUpperCase() !== 'CENTER') return null;
  if (p.style?.padding || p.style?.paddingLeft || p.style?.paddingRight) return null;

  const justify = p.justify?.toUpperCase();
  if (justify === 'LEFT' || justify === 'START') return style.padStart;
  if (justify === 'RIGHT' || justify === 'END') return style.padEnd;
  return null;
}

interface GridRowProps {
  /** 그 행의 자료. **이것이 바뀌지 않으면 행은 다시 그리지 않는다.** */
  item: any;
  /** 자료 기준 행 번호. 에이전트가 `data-el-row` 로 이 행을 짚는다. */
  index: number;
  headers: Array<GridLayoutHeader>;
  /** headers 와 같은 순서의 셀 스타일. 폭이 바뀔 때만 새로 만든다. */
  cellStyles: Array<React.CSSProperties>;
  scope: any;
  /**
   * 바깥이 다시 그려진 횟수.
   *
   * 셀은 `render(item, scope, i)` 로 **행 밖의 값**(scope.state)도 읽는다.
   * 그래서 자료가 그대로여도 페이지가 다시 그려졌으면 셀도 다시 그려야 한다 —
   * 안 그러면 탭을 재사용하는 화면에서 옛 값이 그대로 굳는다.
   */
  epoch: number;
  focused: boolean;
  /** 그룹 첫 행이 아니어서 그룹 열을 비울 것인가. */
  grouped: boolean;
  onFocus: (index: number) => void;
  onDoubleClick: (index: number) => void;
}

/**
 * 표의 한 행.
 *
 * `memo` 인 것이 핵심이다. 포커스 이동·가로 스크롤처럼 GridLayout 이 제 상태만
 * 바꿀 때 보이는 행 전부를 다시 만들던 것을 여기서 끊는다.
 */
export const GridRow = React.memo((props: GridRowProps) => {
  const {
    item, index, headers, cellStyles, scope, focused, grouped, onFocus, onDoubleClick,
  } = props;

  return (
    <FlexLayout
      elRow={String(index)}
      onClick={() => onFocus(index)}
      onDoubleClick={() => onDoubleClick(index)}
      className={focused ? style.highlight : undefined}
    >
      {headers.map((x: GridLayoutHeader, y) => {
        // eslint-disable-next-line no-nested-ternary
        const cell = (x.group && grouped)
          ? <div/>
          : (x.render
            ? x.render(item, scope, index)
            : <FlexLayout align="center" justify="center">{item[x.id]}</FlexLayout>);

        return (
          <FlexLayout
            key={x.id}
            elCell={x.id}
            className={JoinClassName.make([paddingClassOf(cell)])}
            style={tintedCellStyle(cellStyles[y], cell)}
          >
            {cell}
          </FlexLayout>
        );
      })}
    </FlexLayout>
  );
});

interface GridSumRowProps {
  item: any;
  data: Array<any>;
  headers: Array<GridLayoutHeader>;
  cellStyles: Array<React.CSSProperties>;
  scope: any;
  epoch: number;
}

/** 그룹 소계 행. */
export const GridSumRow = React.memo((props: GridSumRowProps) => {
  const {
    item, data, headers, cellStyles, scope,
  } = props;

  return (
    <FlexLayout className={style.group}>
      {headers.map((x: GridLayoutHeader, y) => {
        const cell = x.sum && x.sum(item, data, scope);

        return (
          <FlexLayout
            key={x.id}
            className={JoinClassName.make([paddingClassOf(cell)])}
            style={tintedCellStyle(cellStyles[y], cell)}
          >
            {cell}
          </FlexLayout>
        );
      })}
    </FlexLayout>
  );
});
